import React from 'react';
import { Navigate, useOutletContext } from 'react-router-dom';

import { ContentSegmentDocumentMetadata } from '@candu-ai/content/types';
import { TrackingRoute } from 'commons/eventing';
import NotFound from 'dashboard/pages/NotFound';

import ContentAnalytics, { ANALYTICS_ROUTE } from './pages/Analytics';
import ContentNotebook, { NOTEBOOK_ROUTE } from './pages/Notebook';
import { contentSegmentsRoutes, SEGMENTS_ROUTE, SegmentsLayout } from './pages/Segments';
import ContentSettings, { contentSettingsRoute, PLACEMENTS_AND_EMBEDS_ROUTE, SETTINGS_ROUTE } from './pages/Settings';
import { EMBEDS_ROUTE } from './pages/Settings/pages/Embeds';
import { PLACEMENTS_ROUTE } from './pages/Settings/pages/Placements';

type WithContextProps = {
  children: ({
    contentId,
    mergedContentSegments,
    withPublishModal,
    editSnapshotPlacementId,
    showPlacementsTabOnLoad,
  }: {
    contentId: string;
    mergedContentSegments: ContentSegmentDocumentMetadata;
    withPublishModal: boolean;
    editSnapshotPlacementId?: string;
    showPlacementsTabOnLoad: boolean;
  }) => JSX.Element;
  pageName: string;
  primaryId?: string;
};

const WithContext = ({ children, ...props }: WithContextProps) => {
  const urlProps = useOutletContext<{
    contentId: string;
    mergedContentSegments: ContentSegmentDocumentMetadata;
    withPublishModal: boolean;
    editSnapshotPlacementId?: string;
    showPlacementsTabOnLoad: boolean;
  }>();
  const {
    contentId,
    mergedContentSegments,
    withPublishModal,
    editSnapshotPlacementId,
    showPlacementsTabOnLoad,
  } = urlProps || {};

  return (
    <TrackingRoute {...props} properties={{ contentId }}>
      {children({
        contentId,
        mergedContentSegments,
        withPublishModal,
        editSnapshotPlacementId,
        showPlacementsTabOnLoad,
      })}
    </TrackingRoute>
  );
};

const ValidSegmentId = () => {
  const outletContext = useOutletContext<{
    mergedContentSegments: any[] | undefined;
    withPublishModal: boolean;
    editSnapshotPlacementId?: string;
    showPlacementsTabOnLoad: boolean;
  }>();
  if (!outletContext?.mergedContentSegments) {
    return <NotFound />;
  }
  return (
    <Navigate
      to={`${outletContext.mergedContentSegments[0]?.segmentId}?${
        outletContext.withPublishModal ? `&withPublishModal` : ''
      }${
        outletContext.editSnapshotPlacementId
          ? `&editSnapshotPlacementId=${outletContext.editSnapshotPlacementId}`
          : ''
      }${outletContext.showPlacementsTabOnLoad ? `&showPlacementsTabOnLoad` : ''}`}
      replace
    />
  );
};

export const contentEditorRoutes = [
  {
    path: '',
    element: (
      <WithContext pageName="editor.root">
        {({ withPublishModal, editSnapshotPlacementId, showPlacementsTabOnLoad }) => (
          <Navigate
            to={`${NOTEBOOK_ROUTE.path}?${withPublishModal ? `&withPublishModal` : ''}${
              editSnapshotPlacementId ? `&editSnapshotPlacementId=${editSnapshotPlacementId}` : ''
            }${showPlacementsTabOnLoad ? `&showPlacementsTabOnLoad` : ''}`}
            replace
          />
        )}
      </WithContext>
    ),
  },
  {
    path: NOTEBOOK_ROUTE.path,
    element: <ValidSegmentId />,
  },
  {
    path: `${NOTEBOOK_ROUTE.path}/:segmentId/*`,
    element: (
      <WithContext pageName="editor.notebook" primaryId="segmentId">
        {({ contentId, mergedContentSegments }) => (
          <ContentNotebook contentId={contentId} mergedContentSegments={mergedContentSegments} />
        )}
      </WithContext>
    ),
  },
  {
    path: `${PLACEMENTS_AND_EMBEDS_ROUTE.path}/${EMBEDS_ROUTE.path}`,
    element: <Navigate to={`../${SETTINGS_ROUTE.path}/${EMBEDS_ROUTE.path}`} replace />,
  },
  {
    path: `${PLACEMENTS_AND_EMBEDS_ROUTE.path}/*`,
    element: <Navigate to={`../${SETTINGS_ROUTE.path}/${PLACEMENTS_ROUTE.path}`} replace />,
  },
  {
    path: `${SETTINGS_ROUTE.path}/*`,

    element: (
      <WithContext pageName="editor.settings">
        {({ contentId }) => <ContentSettings contentId={contentId} />}
      </WithContext>
    ),
    children: contentSettingsRoute,
  },
  {
    path: `${SEGMENTS_ROUTE.path}/*`,

    element: (
      <WithContext pageName="editor.segments">
        {({ contentId }) => <SegmentsLayout contentId={contentId} />}
      </WithContext>
    ),
    children: contentSegmentsRoutes,
  },
  {
    path: ANALYTICS_ROUTE.path,

    element: (
      <WithContext pageName="editor.analytics">
        {({ contentId }) => <ContentAnalytics contentId={contentId} />}
      </WithContext>
    ),
  },
  {
    path: '*',
    element: <NotFound />,
  },
];
